import { Link, ListItemText, Typography } from "@mui/material";
import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";

import {
  StyledContentList,
  StyledContentListItem,
  StyledContentParagraph,
  StyledContentPageTitle,
} from "src/components";
import { ariaLabel } from "src/constants";
import ContentPageWrapper from "src/containers/ContentPageWrapper";
import { BrandCodeEnum } from "src/graphql/generated/types";
import { useBrand } from "src/hooks/useBrand";

const Accessibility = () => {
  const title = "Déclaration d’accessibilité";
  const { customerPortal, identity, code } = useBrand();
  const navigate = useNavigate();
  const redirectToCustomerPortal = code !== BrandCodeEnum.Foncia;

  useEffect(() => {
    if (code && redirectToCustomerPortal) {
      navigate("/");
    }
  }, [code, redirectToCustomerPortal, navigate]);

  return (
    <ContentPageWrapper title={title}>
      <StyledContentParagraph>
        {identity.name}{" "}
        <span>
          s'engage à fournir un site internet et espace client accessible au plus grand nombre,
          indépendamment de la technologie ou des capacités, conformément à l'article 47 de la loi
          française 2005-102 du 11 février 2005.
        </span>
      </StyledContentParagraph>
      <StyledContentParagraph>
        Nous travaillons activement à l'amélioration de l'accessibilité et de la facilité
        d'utilisation de notre site web et, ce faisant, nous adhérons à un grand nombre de normes et
        de directives disponibles.
      </StyledContentParagraph>
      <StyledContentParagraph>
        <span>
          Conformément au décret 2019-768 du 24 juillet 2019 sur l’accessibilité numérique des
          entreprises et services publics, nous publions :
        </span>
        <StyledContentList disablePadding>
          <StyledContentListItem disablePadding>
            <ListItemText primary="Notre plan pluriannuel d’accessibilité numérique 2025-2027" />
          </StyledContentListItem>
          <StyledContentListItem disablePadding sx={{ listStyleType: "none" }}>
            <StyledContentList disablePadding variant="circle">
              <StyledContentListItem disablePadding>
                <ListItemText primary="Notre plan annuel d'accessibilité 2025" />
              </StyledContentListItem>
              <StyledContentListItem disablePadding>
                <ListItemText primary="Notre plan annuel d'accessibilité 2026" />
              </StyledContentListItem>
              <StyledContentListItem disablePadding>
                <ListItemText primary="Notre plan annuel d'accessibilité 2027" />
              </StyledContentListItem>
            </StyledContentList>
          </StyledContentListItem>
          <StyledContentListItem disablePadding>
            <ListItemText primary="Nos déclarations d’accessibilité numérique." />
          </StyledContentListItem>
        </StyledContentList>
      </StyledContentParagraph>
      <StyledContentParagraph>
        <span>
          Cette déclaration d’accessibilité s’applique au site Web :{" "}
          <Link
            href={customerPortal.url}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={ariaLabel.customerAreaLinkLabel}
          >
            {customerPortal.url}
          </Link>
        </span>
      </StyledContentParagraph>
      <StyledContentPageTitle role="heading" variant="h4">
        État de conformité
      </StyledContentPageTitle>
      <StyledContentParagraph>
        <span>
          {customerPortal.name} est <strong>partiellement en conformité</strong> avec le référentiel
          général d’amélioration de l’accessibilité. Les non-conformités sont énumérées ci-dessous.
        </span>
      </StyledContentParagraph>
      <StyledContentPageTitle role="heading" variant="h4">
        Résultat des tests
      </StyledContentPageTitle>
      <StyledContentParagraph>
        <span>
          L’audit de conformité au RGAA 4.1.2 réalisé par la société{" "}
          <Link
            target="_blank"
            href="https://www.numerik-ea.fr/"
            rel="noopener noreferrer"
            aria-label={ariaLabel.numerikaLinkLabel}
            sx={{ mr: 0.5 }}
          >
            Numerik-ea
          </Link>
          révèle que :
        </span>
        <StyledContentList disablePadding>
          <StyledContentListItem disablePadding>
            <ListItemText
              primary={
                <span>
                  <strong>80,33%</strong> des critères du RGAA version 4.1.2 sont respectés
                </span>
              }
            />
          </StyledContentListItem>
        </StyledContentList>
      </StyledContentParagraph>
      <StyledContentParagraph>
        <Typography>Nombre de critères conformes : 49</Typography>
        <Typography>Nombre de critères non applicables : 45</Typography>
        <Typography>Nombre de critères non conformes : 12</Typography>
      </StyledContentParagraph>
      <StyledContentPageTitle role="heading" variant="h4">
        Contenus non accessibles
      </StyledContentPageTitle>
      <StyledContentPageTitle variant="h5" role="heading">
        Non-conformités
      </StyledContentPageTitle>
      <StyledContentParagraph>
        <span>
          Plusieurs éléments de non-conformité décrit ci-dessous sont pour certains récurrents sur
          plusieurs pages. Sont listées ci-dessous les principales non-conformités du site.
        </span>
        <StyledContentList disablePadding>
          <StyledContentListItem disablePadding>
            <ListItemText
              primary={
                <span>
                  3.2 Dans chaque page web, le contraste entre la couleur du texte et la couleur de
                  son arrière-plan est-il suffisamment élevé (hors cas particuliers)
                </span>
              }
            />
          </StyledContentListItem>
          <StyledContentListItem disablePadding>
            <ListItemText
              primary={
                <span>
                  7.1 Chaque script est-il, si nécessaire, compatible avec les technologies
                  d’assistance
                </span>
              }
            />
          </StyledContentListItem>
          <StyledContentListItem disablePadding>
            <ListItemText
              primary={
                <span>
                  7.3 Chaque script est-il contrôlable par le clavier et par tout dispositif de
                  pointage (hors cas particuliers)
                </span>
              }
            />
          </StyledContentListItem>
          <StyledContentListItem disablePadding>
            <ListItemText
              primary={
                <span>
                  8.9 Dans chaque page web, les balises ne doivent pas être utilisées uniquement à
                  des fins de présentation. Cette règle est-elle respectée
                </span>
              }
            />
          </StyledContentListItem>
          <StyledContentListItem disablePadding>
            <ListItemText
              primary={
                <span>
                  9.1 Dans chaque page web, l’information est-elle structurée par l’utilisation
                  appropriée de titres
                </span>
              }
            />
          </StyledContentListItem>
          <StyledContentListItem disablePadding>
            <ListItemText
              primary={
                <span>
                  10.7 Dans chaque page web, pour chaque élément recevant le focus, la prise de
                  focus est-elle visible
                </span>
              }
            />
          </StyledContentListItem>
          <StyledContentListItem disablePadding>
            <ListItemText
              primary={<span>11.1 Chaque champ de formulaire a-t-il une étiquette</span>}
            />
          </StyledContentListItem>
          <StyledContentListItem disablePadding>
            <ListItemText
              primary={
                <span>
                  11.10 Dans chaque formulaire, le contrôle de saisie est-il utilisé de manière
                  pertinente (hors cas particuliers)
                </span>
              }
            />
          </StyledContentListItem>
          <StyledContentListItem disablePadding>
            <ListItemText
              primary={
                <span>
                  11.13 La finalité d’un champ de saisie peut-elle être déduite pour faciliter le
                  remplissage automatique des champs avec les données de l’utilisateur
                </span>
              }
            />
          </StyledContentListItem>
          <StyledContentListItem disablePadding>
            <ListItemText
              primary={
                <span>
                  12.7 Dans chaque page web, un lien d’évitement ou d’accès rapide à la zone de
                  contenu principal est-il présent (hors cas particuliers)
                </span>
              }
            />
          </StyledContentListItem>
          <StyledContentListItem disablePadding>
            <ListItemText
              primary={
                <span>12.8 Dans chaque page web, l’ordre de tabulation est-il cohérent</span>
              }
            />
          </StyledContentListItem>
          <StyledContentListItem disablePadding>
            <ListItemText
              primary={
                <span>
                  13.3 Dans chaque page web, chaque document bureautique en téléchargement
                  possède-t-il, si nécessaire, une version accessible (hors cas particuliers)
                </span>
              }
            />
          </StyledContentListItem>
        </StyledContentList>
      </StyledContentParagraph>
      <StyledContentPageTitle variant="h5" role="heading">
        Dérogations pour charge disproportionnée
      </StyledContentPageTitle>
      <StyledContentParagraph>Aucunes</StyledContentParagraph>
      <StyledContentPageTitle variant="h5" role="heading">
        Contenus non soumis à l’obligation d’accessibilité
      </StyledContentPageTitle>
      <StyledContentParagraph>Aucuns</StyledContentParagraph>
      <StyledContentPageTitle role="heading" variant="h4">
        Établissement de cette déclaration d'accessibilité
      </StyledContentPageTitle>
      <StyledContentParagraph>
        Cette déclaration a été établie le 05 avril 2024. Elle a été mise à jour le 18 août 2025.
      </StyledContentParagraph>
      <StyledContentPageTitle role="heading" variant="h5">
        Technologies utilisées pour la réalisation du site
      </StyledContentPageTitle>
      <StyledContentList>
        <StyledContentListItem disablePadding>
          <ListItemText primary="HTML5" />
        </StyledContentListItem>
        <StyledContentListItem disablePadding>
          <ListItemText primary="CSS" />
        </StyledContentListItem>
        <StyledContentListItem disablePadding>
          <ListItemText primary="JavaScript(ReactJS)" />
        </StyledContentListItem>
      </StyledContentList>
      <StyledContentPageTitle role="heading" variant="h5">
        Environnement de test
      </StyledContentPageTitle>
      <StyledContentParagraph>
        <span>
          L'audit a été effectué sur la base du référentiel RGAA 4.1.2 dont la partie technique est
          composée de:
        </span>
        <StyledContentList disablePadding>
          <StyledContentListItem disablePadding>
            <ListItemText primary="106 critères " />
          </StyledContentListItem>
          <StyledContentListItem disablePadding>
            <ListItemText primary="258 tests unitaires " />
          </StyledContentListItem>
        </StyledContentList>
        <span>Le RGAA 4.1.2 : </span>
        <Link
          href="https://accessibilite.numerique.gouv.fr"
          target="_blank"
          rel="noopener noreferrer"
          aria-label={ariaLabel.rgaaLinkLabel}
        >
          https://accessibilite.numerique.gouv.fr
        </Link>
      </StyledContentParagraph>
      <StyledContentParagraph>
        <span>
          Les tests des pages web ont été effectués avec les combinaisons de navigateurs web et
          lecteurs d’écran suivants :
        </span>
        <StyledContentList>
          <StyledContentListItem disablePadding>
            <ListItemText primary="NVDA, Firefox" />
          </StyledContentListItem>
          <StyledContentListItem disablePadding>
            <ListItemText primary="Jaws , Firefox" />
          </StyledContentListItem>
          <StyledContentListItem disablePadding>
            <ListItemText primary="Voiceover, Safari" />
          </StyledContentListItem>
        </StyledContentList>
      </StyledContentParagraph>
      <StyledContentPageTitle role="heading" variant="h5">
        Outils utilisés pour évaluer l’accessibilité
      </StyledContentPageTitle>
      <StyledContentList>
        <StyledContentListItem disablePadding>
          <ListItemText primary="WCAG contrast checker " />
        </StyledContentListItem>
        <StyledContentListItem disablePadding>
          <ListItemText primary="HeadingsMap" />
        </StyledContentListItem>
        <StyledContentListItem disablePadding>
          <ListItemText primary="Web developer" />
        </StyledContentListItem>
        <StyledContentListItem disablePadding>
          <ListItemText primary="ARC Toolkit" />
        </StyledContentListItem>
        <StyledContentListItem disablePadding>
          <ListItemText primary="PAC (PDF Accessibility Checker)" />
        </StyledContentListItem>
      </StyledContentList>
      <StyledContentPageTitle role="heading" variant="h5">
        Pages du site ayant fait l'objet de la vérification de conformité
      </StyledContentPageTitle>
      <StyledContentList>
        <StyledContentListItem disablePadding>
          <ListItemText primary={`Espace ${customerPortal.name}`} />
        </StyledContentListItem>
        <StyledContentListItem disablePadding>
          <ListItemText primary="Connexion" />
        </StyledContentListItem>
        <StyledContentListItem disablePadding>
          <ListItemText primary="Mon compte copropriétaire" />
        </StyledContentListItem>
        <StyledContentListItem disablePadding>
          <ListItemText primary="Mon compte bailleur" />
        </StyledContentListItem>
        <StyledContentListItem disablePadding>
          <ListItemText primary="Mon profil > Mes identifiants (compte copropriétaire)" />
        </StyledContentListItem>
        <StyledContentListItem disablePadding>
          <ListItemText primary="Mon profil > Confidentialité (compte copropriétaire)" />
        </StyledContentListItem>
        <StyledContentListItem disablePadding>
          <ListItemText primary="Mes demandes (compte copropriétaire)" />
        </StyledContentListItem>
        <StyledContentListItem disablePadding>
          <ListItemText primary="Mes demandes > Répondre à la demande T000XS4A0 (compte copropriétaire)" />
        </StyledContentListItem>
        <StyledContentListItem disablePadding>
          <ListItemText primary="Ma comptabilité (compte copropriétaire)" />
        </StyledContentListItem>
        <StyledContentListItem disablePadding>
          <ListItemText primary="Mes paiements > Souscription au prélèvement automatique (compte copropriétaire)" />
        </StyledContentListItem>
        <StyledContentListItem disablePadding>
          <ListItemText primary="Offres et services (compte copropriétaire)" />
        </StyledContentListItem>
        <StyledContentListItem disablePadding>
          <ListItemText primary="Mon conseil syndical > Travaux et interventions (compte copropriétaire) " />
        </StyledContentListItem>
        <StyledContentListItem disablePadding>
          <ListItemText primary="Mon conseil syndical > Comptes de l’immeuble (compte copropriétaire) " />
        </StyledContentListItem>
        <StyledContentListItem disablePadding>
          <ListItemText primary="Mes documents (compte copropriétaire)" />
        </StyledContentListItem>
        <StyledContentListItem disablePadding>
          <ListItemText primary="Mes documents > Appels de provisions (compte copropriétaire) " />
        </StyledContentListItem>
      </StyledContentList>
      <StyledContentPageTitle role="heading" variant="h4">
        Retour d'information et contact
      </StyledContentPageTitle>
      <StyledContentParagraph>
        <span>
          Si vous n’arrivez pas à accéder à un contenu ou à un service, vous pouvez contacter{" "}
        </span>
        <Link
          target="_blank"
          rel="noopener noreferrer"
          href={`mailto:${customerPortal.emails?.accessibility}`}
        >
          {customerPortal.emails?.accessibility}
        </Link>{" "}
        <span>
          pour être orienté vers une alternative accessible ou obtenir le contenu sous une autre
          forme. Nous essayons de répondre dans les 10 jours ouvrés.
        </span>
      </StyledContentParagraph>
      <StyledContentPageTitle role="heading" variant="h4">
        Voies de recours
      </StyledContentPageTitle>
      <StyledContentParagraph>
        Cette procédure est à utiliser dans le cas suivant:{" "}
      </StyledContentParagraph>
      <StyledContentParagraph>
        Vous avez signalé au responsable du service internet un défaut d'accessibilité qui vous
        empêche d'accéder à un contenu ou à un des services du portail et vous n'avez pas obtenu de
        réponse satisfaisante.
      </StyledContentParagraph>
      <StyledContentParagraph>
        Plusieurs moyens sont à votre disposition :{" "}
        <StyledContentList disablePadding>
          <StyledContentListItem disablePadding>
            <ListItemText>
              <span>Écrire un message au Défenseur des droits (</span>
              <Link
                href="https://www.defenseurdesdroits.fr/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label={ariaLabel.rightsProtectorLinkLabel}
              >
                formulaire sur le site du défenseur des droits
              </Link>
              )
            </ListItemText>
          </StyledContentListItem>

          <StyledContentListItem disablePadding>
            <ListItemText>
              <span>Contacter le délégué du Défenseur des droits dans votre région (</span>
              <Link
                href="https://www.defenseurdesdroits.fr/carte-des-delegues"
                target="_blank"
                rel="noopener noreferrer"
                aria-label={ariaLabel.delegateDirectoryRightLabel}
              >
                trouver un délégué sur le site du défenseur des droits
              </Link>
              <span>)</span>
            </ListItemText>
          </StyledContentListItem>

          <StyledContentListItem disablePadding>
            <ListItemText>
              <span>Envoyer un courrier par la poste (gratuit, ne pas mettre de timbre) :</span>
              <Typography textAlign="center" paddingTop={2}>
                Défenseur des droits
              </Typography>
              <Typography textAlign="center">Libre réponse 71120</Typography>
              <Typography textAlign="center">75342 Paris CEDEX 07</Typography>
            </ListItemText>
          </StyledContentListItem>
        </StyledContentList>
      </StyledContentParagraph>
    </ContentPageWrapper>
  );
};

export default Accessibility;
